import { useState, useEffect } from 'react';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import api from '../utils/api';
import { validateFormField, isHoneypotFilled, HONEYPOT_FIELD, checkRateLimit, markFormLoaded, isSubmitTooFast, sanitizeText } from '../utils/formValidation';

export default function NewsletterSignup({ source = 'blog', variant = 'default' }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fieldError, setFieldError] = useState('');

  useEffect(() => { markFormLoaded(`newsletter-${source}`); }, [source]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isHoneypotFilled(honeypot)) return;
    if (isSubmitTooFast(`newsletter-${source}`)) return;
    if (!checkRateLimit(`newsletter-${source}`, 3000)) { setFieldError('Please wait a moment.'); return; }
    if (!email.trim()) return;
    const emailCheck = validateFormField(email, 'email');
    if (!emailCheck.valid) { setFieldError(emailCheck.error); return; }
    if (name.trim()) {
      const nameCheck = validateFormField(name, 'name');
      if (!nameCheck.valid) { setFieldError(nameCheck.error); return; }
    }
    setFieldError('');
    setLoading(true);
    try {
      const { data } = await api.post('/newsletter/subscribe', { email: sanitizeText(email.trim()), name: sanitizeText(name.trim()), source });
      setStatus(data);
      if (data.status === 'subscribed' || data.status === 'resubscribed') {
        setEmail('');
        setName('');
      }
    } catch (e) {
      setStatus({ status: 'error', message: 'Something went wrong. Please try again.' });
    } finally { setLoading(false); }
  };

  if (status?.status === 'subscribed' || status?.status === 'resubscribed') {
    return (
      <div className={`text-center ${variant === 'inline' ? 'py-4' : 'py-8'}`} data-testid="newsletter-success">
        <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-3" />
        <p className="text-foreground font-semibold mb-1">{status.message}</p>
        <p className="text-xs text-muted-foreground">New posts delivered to your inbox every week.</p>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2" data-testid="newsletter-form-inline" noValidate>
        <div className="absolute opacity-0 h-0 w-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
          <input type="text" name={HONEYPOT_FIELD} value={honeypot} onChange={(e) => setHoneypot(e.target.value)} autoComplete="off" tabIndex={-1} />
        </div>
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email"
            data-testid="newsletter-email-inline"
            className="w-full pl-10 pr-4 h-11 bg-white/5 border border-white/10 rounded-sm text-foreground text-sm placeholder:text-muted-foreground/50 focus:border-primary focus:ring-1 focus:ring-primary/50 outline-none"
          />
        </div>
        <button type="submit" disabled={loading} data-testid="newsletter-submit-inline"
          className="px-5 h-11 rounded-sm bg-primary text-primary-foreground font-semibold text-sm tracking-wide transition-all duration-300 hover:bg-white hover:text-black whitespace-nowrap flex items-center gap-1.5">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe'}
        </button>
      </form>
    );
  }

  return (
    <div className="bg-white/5 border border-white/10 rounded-sm p-8" data-testid="newsletter-signup-box">
      <div className="text-center mb-6">
        <Mail className="w-8 h-8 text-primary mx-auto mb-3" strokeWidth={1.5} />
        <h3 className="font-serif text-xl mb-2">Stay in the Loop</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Get the latest Writer's Blogck posts delivered to your inbox every week. Ghostwriting tips, publishing insights, and inspiration.
        </p>
      </div>
      {fieldError && <p className="text-xs text-red-400 text-center mb-3">{fieldError}</p>}
      {status?.status === 'error' && (
        <p className="text-sm text-red-400 text-center mb-4">{status.message}</p>
      )}
      {status?.status === 'already_subscribed' && (
        <p className="text-sm text-primary text-center mb-4">{status.message}</p>
      )}
      <form onSubmit={handleSubmit} className="space-y-3" data-testid="newsletter-form" noValidate>
        <div className="absolute opacity-0 h-0 w-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
          <input type="text" name={HONEYPOT_FIELD} value={honeypot} onChange={(e) => setHoneypot(e.target.value)} autoComplete="off" tabIndex={-1} />
        </div>
        <input
          type="text" value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="First name (optional)"
          data-testid="newsletter-name"
          className="w-full px-4 h-11 bg-white/5 border border-white/10 rounded-sm text-foreground text-sm placeholder:text-muted-foreground/50 focus:border-primary outline-none"
        />
        <input
          type="email" required value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          data-testid="newsletter-email"
          className="w-full px-4 h-11 bg-white/5 border border-white/10 rounded-sm text-foreground text-sm placeholder:text-muted-foreground/50 focus:border-primary outline-none"
        />
        <button type="submit" disabled={loading} data-testid="newsletter-submit"
          className="w-full rounded-sm py-3 bg-primary text-primary-foreground font-semibold tracking-wide transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:bg-white hover:text-black flex items-center justify-center gap-2">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Subscribing...</> : 'Subscribe to Writer\'s Blogck'}
        </button>
      </form>
      <p className="text-[10px] text-muted-foreground/50 text-center mt-3">No spam. Unsubscribe anytime.</p>
    </div>
  );
}
